import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { loadCountries } from "../../../../core/ops/geo-ops";
import { createLocation, enableTerminal, setShipFrom, updateLocation } from "../../../../core/ops/location-ops";
import { errorText } from "../../../../core/primitives/errors";
import { hasErrors } from "../../../../core/primitives/form-errors";
import { busyForm, failedForm, idleForm } from "../../../../core/primitives/load-status";
import {
  draftToLocationUpdate,
  draftToNewLocation,
  EMPTY_LOCATION,
  type LocationField,
  locationDraft,
  validateLocationDraft,
} from "../../../../core/primitives/location-form";
import { LocationsSectionActions, LocationsSectionState } from "./locations-section-model";

export type LocationsSectionDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "locationsRepository" | "countriesRepository"
>;

export interface LocationsSectionHost {
  readonly storeId: string;
}

/**
 * Locations, managed after onboarding. Same fields as the wizard step, by
 * construction. The list is the repository the shell keeps loaded for the
 * selected shop; every write marks it stale and the shell fetches it again.
 */
export class LocationsSectionController extends BaseController implements LocationsSectionActions {
  readonly state = new LocationsSectionState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: LocationsSectionDeps,
    readonly host: LocationsSectionHost,
  ) {
    super();
    subtree.put(LocationsSectionState, this.state);
    subtree.put(LocationsSectionActions, this);
    this.sync(() => {
      const repo = deps.locationsRepository;
      const ours = repo.storeId === host.storeId;
      this.state.locations.value = ours ? repo.locations : [];
      this.state.shipFrom.value = ours ? repo.shipFrom : null;
      this.state.status.value = ours ? repo.status : { kind: "loading" };
    }, [deps.locationsRepository]);
    this.sync(() => {
      const repo = deps.countriesRepository;
      if (repo.status.kind === "idle") void loadCountries(deps);
      this.state.countries.value = repo.countries;
      this.state.countriesStatus.value = repo.status;
    }, [deps.countriesRepository]);
    this.sync(() => {
      this.state.addRegions.value = deps.countriesRepository.regionsFor(this.state.addDraft.value.countryCode);
    }, [this.state.addDraft, deps.countriesRepository]);
    this.sync(() => {
      this.state.editRegions.value = deps.countriesRepository.regionsFor(this.state.editDraft.value.countryCode);
    }, [this.state.editDraft, deps.countriesRepository]);
  }

  startAdd = () => {
    this.state.adding.value = true;
    this.state.addDraft.value = EMPTY_LOCATION;
    this.state.addErrors.value = {};
    this.state.addForm.value = idleForm;
    this.state.editingId.value = null;
  };

  cancelAdd = () => {
    this.state.adding.value = false;
  };

  setAddField = (field: LocationField, value: string) => {
    this.state.addDraft.update((d) => ({ ...d, [field]: value }));
    if (this.state.addErrors.value[field]) this.state.addErrors.update(({ [field]: _, ...rest }) => rest);
  };

  add = async () => {
    if (this.state.addForm.value.busy) return;
    const draft = this.state.addDraft.value;
    const errors = validateLocationDraft(draft, this.deps.countriesRepository.regionsFor(draft.countryCode));
    this.state.addErrors.value = errors;
    if (hasErrors(errors)) return;
    this.state.addForm.value = busyForm;
    const r = await createLocation({ ...this.deps, storeId: this.host.storeId, body: draftToNewLocation(draft) });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.addForm.value = failedForm(r.errors);
      return;
    }
    this.state.addForm.value = idleForm;
    this.state.adding.value = false;
  };

  startEdit = (locationId: string) => {
    const location = this.state.locations.value.find((l) => l.id === locationId);
    if (!location) return;
    this.state.editingId.value = locationId;
    this.state.editDraft.value = locationDraft(location);
    this.state.editErrors.value = {};
    this.state.editForm.value = idleForm;
    this.state.adding.value = false;
  };

  cancelEdit = () => {
    this.state.editingId.value = null;
  };

  setEditField = (field: LocationField, value: string) => {
    this.state.editDraft.update((d) => ({ ...d, [field]: value }));
    if (this.state.editErrors.value[field]) this.state.editErrors.update(({ [field]: _, ...rest }) => rest);
  };

  saveEdit = async () => {
    const locationId = this.state.editingId.value;
    if (!locationId || this.state.editForm.value.busy) return;
    const draft = this.state.editDraft.value;
    const errors = validateLocationDraft(draft, this.deps.countriesRepository.regionsFor(draft.countryCode));
    this.state.editErrors.value = errors;
    if (hasErrors(errors)) return;
    this.state.editForm.value = busyForm;
    const r = await updateLocation({
      ...this.deps,
      storeId: this.host.storeId,
      locationId,
      body: draftToLocationUpdate(draft),
    });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.editForm.value = failedForm(r.errors);
      return;
    }
    this.state.editForm.value = idleForm;
    this.state.editingId.value = null;
  };

  setShipFrom = async (locationId: string | null) => {
    if (this.state.shipFromForm.value.busy) return;
    this.state.shipFromForm.value = busyForm;
    const r = await setShipFrom({ ...this.deps, storeId: this.host.storeId, locationId });
    if (this.isDisposed) return;
    this.state.shipFromForm.value = r.ok ? idleForm : failedForm(r.errors);
  };

  enableTerminal = async (locationId: string) => {
    if (this.state.terminalBusyId.value) return;
    this.state.terminalError.value = null;
    this.state.terminalBusyId.value = locationId;
    const r = await enableTerminal({ ...this.deps, storeId: this.host.storeId, locationId });
    if (this.isDisposed) return;
    this.state.terminalBusyId.value = null;
    if (!r.ok) this.state.terminalError.value = { locationId, message: errorText(r.errors) };
  };
}
